<template>
  <ul
    name="items"
    id="item-select"
    :style="parentStyle"
    :aria-label="defaultItem + ' Selected'"
    :class="{ disabled: disabled || isSingleItem, active: active, horizontal:horizontal, vertical:!horizontal }"
  >
    <li
      @click="toggleActive(item)"
      v-for="item in items"
      :key="item"
      :class="itemClass(item)"
      :style="itemStyle()"
    >
      <span class="item">
        {{ item }}
      </span>
      <span class="indicator">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 18 18"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          xml:space="preserve"
          xmlns:serif="http://www.serif.com/"
          class="indicator-icon"

          style="
            fill-rule: evenodd;
            clip-rule: evenodd;
            stroke-linejoin: round;
            stroke-miterlimit: 2;
            
          "
        >
          <path
            id="push-left"
            d="M6.109,1.478C6.253,1.309 6.486,1.249 6.694,1.326C6.902,1.402 7.04,1.601 7.04,1.822L7.04,16.177C7.04,16.402 6.899,16.602 6.687,16.676C6.476,16.751 6.241,16.685 6.099,16.511L0.118,9.134C-0.043,8.935 -0.039,8.65 0.127,8.456L6.109,1.478Z"
          />
          <path
            id="push-right"
            d="M11.891,1.478L17.873,8.456C18.039,8.65 18.043,8.935 17.882,9.134L11.901,16.511C11.759,16.685 11.524,16.751 11.313,16.676C11.101,16.602 10.96,16.402 10.96,16.177L10.96,1.822C10.96,1.601 11.098,1.402 11.306,1.326C11.514,1.249 11.747,1.309 11.891,1.478Z"
          />
        </svg>
      </span>
    </li>
  </ul>
</template>

<script>
export default {
  props: {
    items: {
      type: Object,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    defaultItem: {
      default: null,
    },
    itemHeight: {
      type: Number,
      default: 5.1,
    },
    itemWidth: {
      type: Number,
      default: 5.1,
    },
    disabled: {
      type: Boolean,
    },
    horizontal: {
      type: Boolean,
      default: true,
    },
    activeSelect: {
      type: String,
      required: true,
    },
  },
  watch: {
    activeSelect(value) {
      this.active = value === this.id;
    },
    disabled(value) {
      if (value) {
        this.active = false;
      }
    },
    defaultItem() {
      this.active = false;
    },
  },
  data() {
    return {
      active: false,
      itemHeightpx: 34,
      itemWidthpx: 34,
      fontSizepx: 20,
    };
  },

  computed: {
    isSingleItem() {
      return this.items.length === 1;
    },
    parentStyle() {
      let style = {
        fontSize: this.fontSizepx + "px",
      };
      if (this.horizontal) {
        style[
          "grid-template-columns"
        ] = `repeat(${this.items.length}, ${this.itemWidthpx}px)`;
        style["width"] = this.dynamicDimension;
        style["height"] = this.itemHeightpx + "px";
      } else {
        style[
          "grid-template-rows"
        ] = `repeat(${this.items.length}, ${this.itemHeightpx}px)`;
        style["height"] = this.dynamicDimension;
        style["width"] = this.itemWidthpx + "px";
      }
      return style;
    },
    visibleItems() {
      if (this.active) {
        return this.items;
      }
      return [this.defaultItem];
    },
    openDimension() {
      return (
        (this.horizontal ? this.itemWidthpx : this.itemHeightpx) *
          this.items.length +
        "px"
      );
    },
    closedDimension() {
      return (this.horizontal ? this.itemWidthpx : this.itemHeightpx) + "px";
    },
    dynamicDimension() {
      return this.active ? this.openDimension : this.closedDimension;
    },
    defaultIndex() {
      return this.items.indexOf(this.defaultItem);
    },
  },
  mounted() {
    window.addEventListener("resize", this.updateItemSizes);
    this.$nextTick(() => {
      if (this.defaultItem === null) {
        this.toggleActive(this.items[0], true);
      }
    });
    this.updateItemSizes();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateItemSizes);
  },
  methods: {
    updateItemSizes() {
      requestAnimationFrame(() => {
        const windowHeight = Math.floor(
          0.9875 * (window.visualViewport?.height || window.innerHeight)
        );
        this.itemHeightpx = (windowHeight * this.itemHeight) / 100;
        this.itemWidthpx = (windowHeight * this.itemWidth) / 100;
        this.fontSizepx = this.itemHeightpx * 0.588;
      });
    },
    itemStyle() {
      if (this.horizontal) {
        if (this.active) {
          return { left: "0px" };
        }
        return {
          left: this.itemWidthpx * -this.defaultIndex + "px",
          height: this.itemHeightpx + "px",
        };
      } else {
        if (this.active) {
          return { top: "0px" };
        }
        return {
          top: this.itemHeightpx * -this.defaultIndex + "px",
          width: this.itemWidthpx + "px",
        };
      }
    },
    itemClass(item) {
      return {
        selected: this.isSelectedItem(item),
        collapse: !this.isSelectedItem(item) && !this.active,
      };
    },
    toggleActive(item, initializing = false) {
      if (this.disabled) {
        this.$emit("select-item", null);
        return;
      }
      if (initializing === true) {
        this.active = false;
      } else {
        this.active = !this.active;
      }
      this.$emit("select-item", item, this.id, this.active);
    },
    isSelectedItem(item) {
      return item === this.defaultItem;
    },
    isVisible(item) {
      return this.defaultItem === item || this.active;
    },
  },
};
</script>

<style scoped>
* {
  user-select: none;
  font-family: "Fira Sans", system-ui;
  font-weight: bold;
  color: var(--button-text-color);
}

#item-select {
  --primary-color: #1789a9;
  --border-color: #11657c;
}
.indicator-icon {
  fill: var(--button-text-color);
  overflow: visible;
}

#item-select {
  display: grid;
  padding: 0;
  margin: 0;
  border-radius: 4px;
  z-index: 3;
  background-color: var(--primary-color);
  overflow: hidden;
  border: 1px solid var(--border-color);
  gap: 0;
  position: relative;
  box-sizing: content-box;
  cursor: pointer;
  text-transform: capitalize;
}
li {
  display: flex;
  list-style-type: none;
  align-items: center;
  justify-content: stretch;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: auto;
  overflow: hidden;
  height: 100%;
  position: relative;
}
.vertical li {
  flex-direction: row;
  padding: 0 0.5vh;
}
.horizontal li {
  padding: 0.5vh 0;
  flex-direction: column;
}

.vertical svg {
  transform: rotate(90deg);
}
li .item {
  display: flex;
  flex: 1 0 0;
  justify-content: center;
  align-items: center;
}
li.selected .indicator {
  display: flex;
  height: 40%;
}

.active li.selected .item {
  font-size: 1em !important;
}
ul.horizontal:not(.disabled) li.selected .item {
  font-size: 0.6em;
}

li .indicator,
.active li .indicator,
.disabled li .indicator {
  height: 0;
}

li.collapse {
  color: rgba(0, 0, 0, 0);
}
.items-enter-active,
.items-leave-active,
li,
#item-select,
.indicator,
.item {
  transition: all 0.5s;
}

.items-enter-from,
.items-leave-to {
  opacity: 0;
  width: 0px;
}
.selected {
  background: rgba(255, 255, 255, 0.15);
}
.disabled {
  cursor: default !important;
}

#push-left {
  animation: push-left 2.5s linear infinite;
}

#push-right {
  animation: push-right 2.5s linear infinite;

}
@keyframes push-left {
  0% {
    transform: translateX(0);
  }
  7%,
  12% {
    transform: translateX(-3px);
  }
  25% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes push-right {
  0% {
    transform: translateX(0);
  }
  7%,
  12% {
    transform: translateX(3px);
  }
  25% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
